import { HGIntervals } from "@/common/types";
import {
  endQualifiers,
  hasQualifiers,
  IntervalQualifiers,
  qualifierLabel,
  qualifyName,
  startQualifiers,
} from "@/common/qualifiers";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";

/**
 * Structural rather than `HGTimeRecordPlus`: the names are the only thing this
 * component reads that a record does not always carry. Records out of the
 * timeline have them from `enrichRecords`; one fetched on its own gets them from
 * `intervalNames()`. Either way, nothing here needs a whole record.
 */
interface RecordIntervalNameProps {
  record: (Partial<HGIntervals> & {
    intervalName: string;
    endIntervalName: string | null;
  }) | null;
}

/**
 * One endpoint of a record's time. Qualified dates get a dotted underline and
 * a tooltip, since "c." and "?" mean nothing on their own.
 */
function Endpoint({
  name,
  qualifiers,
}: {
  name: string;
  qualifiers: IntervalQualifiers;
}) {
  const text = qualifyName(name, qualifiers);

  if (!hasQualifiers(qualifiers)) {
    return <span className="text-muted-foreground text-sm">{text}</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-muted-foreground text-sm underline decoration-dotted decoration-muted-foreground/50 underline-offset-[3px]">
          {text}
        </span>
      </TooltipTrigger>
      <TooltipContent>{qualifierLabel(qualifiers)}</TooltipContent>
    </Tooltip>
  );
}

export default function RecordIntervalName({
  record,
}: RecordIntervalNameProps) {
  if (!record || !record.intervalName) return null;

  return (
    <>
      {!record.end_interval && (
        <span className="text-muted-foreground text-sm">Since </span>
      )}
      <Endpoint name={record.intervalName} qualifiers={startQualifiers(record)} />
      {record.endIntervalName && (
        <>
          <span className="text-muted-foreground text-sm"> - </span>
          <Endpoint
            name={record.endIntervalName}
            qualifiers={endQualifiers(record)}
          />
        </>
      )}
    </>
  );
}
